/**
 * Advanced Search Config Adapter
 * Transforms Product Field Registry into Advanced Search field configurations
 */

import { FieldConfig } from '../../../components/TableSearch/types';
import { PRODUCT_FIELDS_REGISTRY } from '../fields';
import { BaseFieldDefinition } from '../../../shared/types/fieldRegistry';
import {
  getContextAwareConfigFromRegistry,
  toStandardAsyncSelectConfig,
} from '../../../shared/utils/contextAwareAsyncSelectConfig';

/**
 * Convert a field definition to Advanced Search field config
 * Handles flattening of relational fields for searching
 */
function toSearchFieldConfig(
  field: BaseFieldDefinition
): FieldConfig | FieldConfig[] {
  // Handle relational fields - use async select for API-based relations
  if (field.isRelation && field.relationConfig) {
    const { displayField, statusField } = field.relationConfig;

    const configs: FieldConfig[] = [];

    // Get context-aware config for search context
    // Search context uses searchField (name) for value (query matching)
    const contextConfig = getContextAwareConfigFromRegistry(
      PRODUCT_FIELDS_REGISTRY,
      field.key as string,
      'search'
    );

    if (contextConfig) {
      // Convert to standard config for component props
      const asyncConfig = toStandardAsyncSelectConfig(contextConfig);

      configs.push({
        key: displayField,
        label: field.label,
        dataType: 'selection', // Still 'selection' but with async config
        apiEndpoint: asyncConfig.apiEndpoint,
        searchField: asyncConfig.searchField,
        valueField: asyncConfig.valueField, // Uses name field for query matching
        labelField: asyncConfig.labelField,
      });
    } else {
      // Fallback to string search if no async config
      configs.push({
        key: displayField,
        label: field.label,
        dataType: 'string',
      });
    }

    // If there's a status field, add it as a separate searchable field
    if (statusField && field.selectionOptions) {
      configs.push({
        key: statusField,
        label: 'Manufacturing Status',
        dataType: 'selection',
        options: field.selectionOptions,
      });
    }

    return configs;
  }

  // Handle array fields (synonyms, replaced_rns) - search as strings
  if (field.dataType === 'array') {
    // Map to singular form for search key
    const searchKey =
      field.key === 'synonyms'
        ? 'product_synonym'
        : field.key === 'replaced_rns'
          ? 'replaced_rn'
          : field.key;

    return {
      key: searchKey as string,
      label: field.label,
      dataType: 'string',
    };
  }

  // Standard fields - map directly
  const dataType =
    field.dataType === 'object'
      ? 'string'
      : field.dataType === 'boolean'
        ? 'selection'
        : (field.dataType as 'string' | 'number' | 'date' | 'selection');

  return {
    key: field.key as string,
    label: field.label,
    dataType,
    options: field.selectionOptions,
  };
}

/**
 * Convert Product Field Registry to Advanced Search field configurations
 * @returns Array of FieldConfig for Advanced Search component
 */
export function toAdvancedSearchConfig(): FieldConfig[] {
  const searchFields = PRODUCT_FIELDS_REGISTRY.filter(f => f.isSearchable);

  const configs: FieldConfig[] = [];

  searchFields.forEach(field => {
    const config = toSearchFieldConfig(field);
    if (Array.isArray(config)) {
      configs.push(...config);
    } else {
      configs.push(config);
    }
  });

  return configs;
}

/**
 * Get searchable field keys (useful for validation)
 */
export function getSearchableFieldKeys(): string[] {
  return toAdvancedSearchConfig().map(config => config.key);
}
